import { translate } from './i18n.js';

const SWIPE_CLOSE_MIN_DISTANCE = 60;
const SWIPE_CLOSE_MAX_DURATION = 800;

export class AppModal {
    constructor(manager) {
        this.manager = manager;
        this.app = manager.app;
        this.modals = new Map();
    }

    mount(contentId, definition, options = {}) {
        const existingModal = this.modals.get(contentId);

        if (existingModal) {
            existingModal.definition = definition;
            this.syncDefinition(existingModal);
            return existingModal;
        }

        const locale = this.app?.locale || document.documentElement.lang || 'fr';
        const title = translate(definition.titleKey, locale);
        const labelTitle = translate(options.labelTitleKey || definition.titleKey, locale);
        const modal = document.createElement('dialog');
        const hasHeader = definition.header !== false;
        const header = hasHeader ? document.createElement('header') : null;
        const icon = hasHeader ? document.createElement('img') : null;
        const heading = hasHeader ? document.createElement('h2') : null;
        const closeButton = this.createCloseButton(locale);
        const scroll = document.createElement('div');
        const content = document.createElement('div');
        const attachedLabel = Number.isInteger(options.labelIndex)
            ? this.createAttachedLabel(
                definition,
                locale,
                labelTitle,
                options.actionId || contentId
            )
            : null;

        modal.className = 'app-modal';
        modal.dataset.modal = contentId;
        modal.setAttribute('open', '');
        scroll.className = 'app-modal__scroll';
        content.className = 'app-modal__content';
        if (hasHeader) {
            modal.setAttribute('aria-labelledby', `app-modal-${contentId}-title`);
            header.className = 'app-modal__header';
            icon.className = 'app-modal__icon';
            icon.src = definition.icon;
            icon.alt = '';
            icon.loading = 'eager';
            icon.setAttribute('aria-hidden', 'true');
            heading.id = `app-modal-${contentId}-title`;
            heading.className = 'app-modal__title';
            heading.textContent = title;
            header.append(icon, heading);
            scroll.append(header, content);
        } else {
            modal.setAttribute('aria-label', title);
            scroll.append(content);
        }

        if (attachedLabel) {
            modal.classList.add('app-modal--attached-label');
            modal.classList.add(`app-modal--label-${options.labelIndex + 1}`);
            modal.style.setProperty('--app-modal-label-index', options.labelIndex);
            modal.append(attachedLabel);
        }
        modal.append(closeButton, scroll);
        const home = this.app?.home?.root || document.getElementById('home');
        const footer = home?.querySelector(':scope > .home-copyright');

        if (home && footer) {
            footer.before(modal);
        } else {
            (home || document.body).append(modal);
        }

        const activeModal = {
            contentId,
            definition,
            modal,
            baseClassName: modal.className,
            body: content,
            header,
            scroll,
            heading,
            icon,
            attachedLabel,
            labelTitleKey: options.labelTitleKey || null,
            closeButton,
            loading: null,
            returnFocus: null,
            cleanup: definition.cleanup,
            swipeStart: null,
            openingFrame: null,
            closingAnimationHandler: null,
            hasOpened: false,
            rendered: false,
            renderPromise: Promise.resolve()
        };
        const keydownHandler = (event) => {
            if (
                event.key !== 'Escape'
                || this.manager.activeModal !== activeModal
                || !this.isClosable(activeModal)
            ) {
                return;
            }
            event.preventDefault();
            this.manager.close(activeModal);
        };

        closeButton.addEventListener('click', () => {
            if (this.isClosable(activeModal)) {
                this.manager.close(activeModal);
            }
        });
        modal.addEventListener('cancel', (event) => {
            event.preventDefault();
            if (this.isClosable(activeModal)) {
                this.manager.close(activeModal);
            }
        });
        this.bindSwipeToClose(activeModal);
        document.addEventListener('keydown', keydownHandler);
        activeModal.keydownHandler = keydownHandler;
        this.modals.set(contentId, activeModal);

        return activeModal;
    }

    open(contentId, definition, returnFocus = null) {
        const locale = this.app?.locale || document.documentElement.lang || 'fr';
        const activeModal = this.mount(contentId, definition);
        const {
            modal,
            body,
            scroll,
            closeButton,
            attachedLabel
        } = activeModal;

        if (
            modal.classList.contains('is-open')
            || modal.classList.contains('is-opening')
        ) {
            return true;
        }

        activeModal.definition = definition;
        activeModal.cleanup = definition.cleanup;
        activeModal.returnFocus = returnFocus || attachedLabel;
        activeModal.rendered = false;
        if (activeModal.closingAnimationHandler) {
            modal.removeEventListener('animationend', activeModal.closingAnimationHandler);
            activeModal.closingAnimationHandler = null;
        }
        modal.className = activeModal.baseClassName;
        body.className = 'app-modal__content';
        body.replaceChildren();
        this.syncDefinition(activeModal);

        this.manager.modalStack = this.manager.modalStack.filter((item) => item !== activeModal);
        this.manager.modalStack.push(activeModal);
        this.manager.activeModal = activeModal;
        this.syncModalStack();

        if (typeof definition.render === 'function') {
            const context = this.createRenderContext(activeModal, locale);

            if (definition.loading) {
                this.setLoading(activeModal, true);
            }
            activeModal.renderPromise = Promise.resolve(definition.render(context))
                .catch((error) => {
                    console.error(`Unable to render modal ${contentId}.`, error);
                })
                .finally(() => {
                    if (definition.loading) {
                        this.setLoading(activeModal, false);
                    }
                    activeModal.rendered = true;
                });
        } else {
            this.renderStaticContent(activeModal, locale);
            activeModal.rendered = true;
            activeModal.renderPromise = Promise.resolve(activeModal);
        }

        this.mountNotificationContainer(modal);
        this.show(activeModal);

        return true;
    }

    show(activeModal) {
        const { modal, closeButton } = activeModal;
        const reveal = () => {
            activeModal.openingFrame = null;

            if (!modal.classList.contains('is-opening')) {
                return;
            }

            modal.classList.remove('is-opening');
            modal.classList.add('is-open');
            closeButton.focus({ preventScroll: true });
        };

        if (activeModal.hasOpened) {
            modal.classList.add('is-open');
            closeButton.focus({ preventScroll: true });
            return;
        }

        activeModal.hasOpened = true;
        modal.classList.add('is-opening');
        activeModal.openingFrame = requestAnimationFrame(() => {
            activeModal.openingFrame = requestAnimationFrame(reveal);
        });
    }

    close(activeModal, onClosed = null) {
        if (
            !activeModal
            || (
                !activeModal.modal.classList.contains('is-open')
                && !activeModal.modal.classList.contains('is-opening')
            )
        ) {
            return false;
        }

        if (activeModal.openingFrame !== null) {
            cancelAnimationFrame(activeModal.openingFrame);
            activeModal.openingFrame = null;
        }
        const closingZIndex = activeModal.modal.style.zIndex
            || window.getComputedStyle(activeModal.modal).zIndex
            || '500';
        const finishClosing = (event) => {
            if (
                event.target !== activeModal.modal
                || event.animationName !== 'app-modal-exit'
                || activeModal.closingAnimationHandler !== finishClosing
            ) {
                return;
            }

            activeModal.modal.removeEventListener('animationend', finishClosing);
            activeModal.closingAnimationHandler = null;
            activeModal.modal.classList.remove('is-closing');
            activeModal.modal.style.removeProperty('z-index');
        };

        activeModal.cleanup?.();
        activeModal.modal.classList.remove('is-open', 'is-opening');
        activeModal.modal.classList.add('is-closing');
        activeModal.modal.style.zIndex = closingZIndex;
        activeModal.closingAnimationHandler = finishClosing;
        activeModal.modal.addEventListener('animationend', finishClosing);
        this.manager.modalStack = this.manager.modalStack.filter(
            (modal) => modal !== activeModal
        );
        this.manager.activeModal = this.manager.modalStack.at(-1) || null;
        this.syncModalStack();
        this.mountNotificationContainer(this.manager.activeModal?.modal || null);
        activeModal.returnFocus?.focus?.({ preventScroll: true });
        this.app?.home?.handleModalClosed?.(activeModal);
        onClosed?.();

        return true;
    }

    syncModalStack() {
        this.modals.forEach(({ modal }) => {
            if (!modal.classList.contains('is-closing')) {
                modal.style.removeProperty('z-index');
            }
        });
        this.manager.modalStack.forEach(({ modal }, index) => {
            modal.style.zIndex = String(500 + index);
        });
    }

    isClosable(activeModal) {
        return activeModal?.definition?.closable !== false;
    }

    bindSwipeToClose(activeModal) {
        const { modal, scroll } = activeModal;
        const resetSwipe = () => {
            activeModal.swipeStart = null;
        };
        const startSwipe = (event) => {
            if (
                event.touches.length !== 1
                || this.manager.activeModal !== activeModal
                || !this.isClosable(activeModal)
                || scroll.scrollTop > 0
            ) {
                resetSwipe();
                return;
            }

            const touch = event.touches[0];

            activeModal.swipeStart = {
                identifier: touch.identifier,
                x: touch.clientX,
                y: touch.clientY,
                time: event.timeStamp
            };
        };
        const endSwipe = (event) => {
            const start = activeModal.swipeStart;

            resetSwipe();
            if (
                !start
                || this.manager.activeModal !== activeModal
                || !this.isClosable(activeModal)
            ) {
                return;
            }

            const touch = Array.from(event.changedTouches).find(
                (changedTouch) => changedTouch.identifier === start.identifier
            );

            if (!touch) {
                return;
            }

            const verticalDistance = touch.clientY - start.y;
            const horizontalDistance = Math.abs(touch.clientX - start.x);
            const duration = event.timeStamp - start.time;

            if (
                verticalDistance >= SWIPE_CLOSE_MIN_DISTANCE
                && horizontalDistance <= verticalDistance
                && duration <= SWIPE_CLOSE_MAX_DURATION
            ) {
                this.manager.close(activeModal);
            }
        };

        modal.addEventListener('touchstart', startSwipe, { passive: true });
        modal.addEventListener('touchend', endSwipe, { passive: true });
        modal.addEventListener('touchcancel', resetSwipe, { passive: true });
    }

    mountNotificationContainer(targetModal = null) {
        const container = document.querySelector('#notification');
        const target = targetModal?.open ? targetModal : document.body;

        if (!container || container.parentElement === target) {
            return false;
        }

        target.append(container);
        return true;
    }

    setLoading(activeModal, visible) {
        if (!activeModal) {
            return false;
        }

        if (visible && !activeModal.loading) {
            activeModal.loading = this.createLoadingLayer(
                this.app?.locale || document.documentElement.lang || 'fr'
            );
            activeModal.modal.append(activeModal.loading);
        }
        if (!activeModal.loading) {
            return false;
        }

        activeModal.modal.setAttribute('aria-busy', String(visible));
        if (!visible) {
            activeModal.loading.remove();
            activeModal.loading = null;
            return true;
        }

        activeModal.loading.hidden = false;
        activeModal.loading.classList.add('is-visible');
        return true;
    }

    createRenderContext(activeModal, locale) {
        return {
            app: this.app,
            body: activeModal.body,
            contentId: activeModal.contentId,
            definition: activeModal.definition,
            header: activeModal.header,
            scroll: activeModal.scroll,
            locale,
            modal: activeModal.modal,
            close: (onClosed = null) => this.manager.close(activeModal, onClosed),
            open: (targetContentId, targetFocus = null) => (
                this.manager.open(targetContentId, targetFocus)
            ),
            showLoading: () => this.setLoading(activeModal, true),
            hideLoading: () => this.setLoading(activeModal, false),
            setTitle: (value) => {
                if (activeModal.heading) {
                    activeModal.heading.textContent = value;
                } else {
                    activeModal.modal.setAttribute('aria-label', value);
                }
            },
            setIcon: (source) => {
                if (activeModal.icon) {
                    activeModal.icon.src = source;
                }
            },
            setContentId: (value) => {
                if (!value || value === activeModal.contentId) {
                    return;
                }

                const targetDefinition = this.manager.contents[value];
                const route = String(targetDefinition?.path || '').replace(/^\/+|\/+$/g, '');

                activeModal.contentId = value;
                activeModal.modal.dataset.modal = value;
                if (activeModal.heading) {
                    activeModal.heading.id = `app-modal-${value}-title`;
                    activeModal.modal.setAttribute('aria-labelledby', activeModal.heading.id);
                }
                if (activeModal.attachedLabel && route) {
                    activeModal.attachedLabel.href = `/${locale}/${route}`;
                }
            }
        };
    }

    syncDefinition(activeModal) {
        const {
            definition,
            contentId,
            modal,
            heading,
            icon,
            attachedLabel,
            closeButton
        } = activeModal;
        const locale = this.app?.locale || document.documentElement.lang || 'fr';
        const title = translate(definition.titleKey, locale);
        const labelTitle = translate(activeModal.labelTitleKey || definition.titleKey, locale);
        const route = String(definition.path || '').replace(/^\/+|\/+$/g, '');
        const labelText = attachedLabel?.querySelector('.app-modal__attached-label-text');
        const labelIcon = attachedLabel?.querySelector('img');

        activeModal.cleanup = definition.cleanup;
        modal.dataset.modal = contentId;
        if (heading) {
            heading.textContent = title;
        } else {
            modal.setAttribute('aria-label', title);
        }
        if (icon) icon.src = definition.icon;
        closeButton.hidden = !this.isClosable(activeModal);
        if (labelText) labelText.textContent = labelTitle;
        if (labelIcon) labelIcon.src = definition.icon;
        if (attachedLabel && route) attachedLabel.href = `/${locale}/${route}`;
    }

    createAttachedLabel(definition, locale, title, actionId) {
        const label = document.createElement('a');
        const icon = document.createElement('img');
        const labelGroup = document.createElement('span');
        const text = document.createElement('span');
        const route = String(definition.path || '').replace(/^\/+|\/+$/g, '');

        label.className = 'app-modal__attached-label';
        label.dataset.homeAction = actionId;
        label.href = `/${locale}/${route}`;
        icon.src = definition.icon;
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        labelGroup.className = 'app-modal__attached-label-group';
        text.className = 'app-modal__attached-label-text';
        text.textContent = title;
        labelGroup.append(text);
        if (definition.filterBadge) {
            const badge = document.createElement('span');

            badge.className = 'home-filter-badge home-filter-badge-tab';
            badge.dataset.filterBadge = 'tab';
            badge.hidden = true;
            labelGroup.append(badge);
        }
        label.append(icon, labelGroup);

        return label;
    }

    createCloseButton(locale) {
        const button = document.createElement('button');
        const icon = document.createElement('img');

        button.type = 'button';
        button.className = 'app-modal__close';
        button.setAttribute('aria-label', translate('eventModal.close', locale));
        icon.src = '/images/close-grey.svg';
        icon.alt = '';
        icon.loading = 'eager';
        icon.setAttribute('aria-hidden', 'true');
        button.append(icon);

        return button;
    }

    createLoadingLayer(locale) {
        const loading = document.createElement('div');
        const label = document.createElement('p');
        const spinner = document.createElement('span');

        loading.className = 'app-modal__busy';
        loading.hidden = true;
        loading.setAttribute('aria-live', 'polite');
        label.textContent = translate('simpleModal.loading', locale);
        spinner.className = 'app-modal__spinner';
        spinner.setAttribute('aria-hidden', 'true');
        loading.append(label, spinner);

        return loading;
    }

    renderStaticContent(activeModal, locale) {
        const { body, definition } = activeModal;

        this.getLocalizedContent(definition, locale).forEach((paragraphText) => {
            const paragraph = document.createElement('p');

            paragraph.textContent = paragraphText;
            body.append(paragraph);
        });

        if (definition.documents?.length) {
            this.loadDocuments(definition, body, locale);
        }
    }

    async loadDocuments(definition, content, locale) {
        const status = document.createElement('p');

        status.className = 'about-modal-status';
        status.setAttribute('role', 'status');
        status.textContent = translate('simpleModal.documentsLoading', locale);
        content.append(status);

        try {
            const sections = await Promise.all(definition.documents.map((documentDefinition) => (
                this.loadDocumentSection(
                    documentDefinition,
                    locale,
                    definition.documentSectionClass || 'about-modal-section'
                )
            )));

            status.replaceWith(...sections);
        } catch (error) {
            console.error('Unable to load app modal documents.', error);
            status.textContent = translate('simpleModal.documentsError', locale);
        }
    }

    async loadDocumentSection(documentDefinition, locale, className) {
        const url = documentDefinition.url.replace('{locale}', encodeURIComponent(locale));
        let response = await fetch(url, {
            headers: { Accept: 'text/html' }
        });

        if (!response.ok && locale !== 'fr') {
            response = await fetch(documentDefinition.url.replace('{locale}', 'fr'), {
                headers: { Accept: 'text/html' }
            });
        }
        if (!response.ok) {
            throw new Error(`Unable to load ${url}: ${response.status}`);
        }

        const sourceDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
        const sourceSection = sourceDocument.querySelector(documentDefinition.selector);

        if (!sourceSection) {
            throw new Error(`Section ${documentDefinition.selector} not found in ${url}`);
        }

        const section = document.createElement('section');

        section.className = className;
        Array.from(sourceSection.childNodes).forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE && ['H2', 'H3'].includes(node.tagName)) {
                const heading = document.createElement(node.tagName === 'H2' ? 'h3' : 'h4');

                heading.textContent = node.textContent;
                section.append(heading);
                return;
            }
            section.append(document.importNode(node, true));
        });

        return section;
    }

    getLocalizedContent(definition, locale) {
        return definition.content?.[locale]
            || definition.content?.fr
            || [];
    }
}
